import { useState, type FormEvent, type ReactElement } from "react";
import { Modal } from "@/components/ui/Modal";

interface Props {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (accessToken: string) => void;
}

const MIN_TOKEN_LENGTH = 20;
const FORM_ID = "dhan-token-form";

/** Dhan has no login redirect: the operator generates a 24-hour token on web.dhan.co and pastes it
 * here. It goes straight to the server in the POST body and is never shown again. */
export function DhanTokenDialog({ open, busy, onClose, onSubmit }: Readonly<Props>): ReactElement {
  const [token, setToken] = useState("");
  const ready = token.trim().length >= MIN_TOKEN_LENGTH;
  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!ready) return;
    onSubmit(token.trim());
    setToken("");
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Connect Dhan"
      description="Paste the 24-hour access token. The server renews it every morning at 08:05 IST."
      icon="ti-key"
      size="lg"
      footer={<DialogButtons busy={busy} ready={ready} onClose={onClose} />}
    >
      <form id={FORM_ID} onSubmit={submit} className="flex flex-col gap-2">
        <label className="text-xs text-ink-secondary" htmlFor="dhan-token">
          Access token
        </label>
        <input
          id="dhan-token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          className="input font-mono"
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
          }}
          placeholder="Paste the token here"
        />
        <p className="text-xs text-ink-muted">
          web.dhan.co → My Profile → Access DhanHQ APIs → Generate Access Token (24 hours).
        </p>
      </form>
    </Modal>
  );
}

function DialogButtons({
  busy,
  ready,
  onClose,
}: Readonly<{ busy: boolean; ready: boolean; onClose: () => void }>): ReactElement {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button type="submit" form={FORM_ID} className="btn btn-primary" disabled={busy || !ready}>
        Connect
      </button>
    </>
  );
}
